<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount, nextTick, useTemplateRef } from 'vue'
import ExamCard from '@/components/Head/ExamCard.vue'
import { userModule } from '@/store/user/module'
import type { Study } from '@pocketprep/types'
import type { ClientExam } from '@/store/bundle/state'
import { examMetadataModule } from '@/store/examMetadata/module'
import { bundleModule } from '@/store/bundle/module'
import UIKit from '@pocketprep/ui-kit'
import AddExamSidePanel from '@/components/Settings/AddExamSidePanel.vue'
import ExamMenuDropdown from '@/components/Head/ExamMenuDropdown.vue'
import type { ComponentPublicInstance } from 'vue'
import { quizModule } from '@/store/quiz/module'

const {
    ModalContainer,
    Modal,
} = UIKit

const props = defineProps<{
    currentExamMetadata: Study.Class.ExamMetadataJSON | ClientExam
    examMetadata: (Study.Class.ExamMetadataJSON | ClientExam)[]
}>()

const emit = defineEmits<{
    examChanged: []
}>()

const showDropdown = ref(false)
const isLoading = ref(false)
const showAddExamSidePanel = ref(false)
const shouldFocusFirstCard = ref(false)

const dropdownBtn = useTemplateRef<ComponentPublicInstance>('dropdownBtn')
const examMenuDropdown = useTemplateRef<ComponentPublicInstance>('examMenuDropdown')

const isDarkMode = computed(() => {
    return userModule.state.settings.isDarkMode
})

onMounted(() => {
    examMetadataModule.actions.fetchExamMetadata()
    bundleModule.actions.fetchBundles()
    userModule.actions.fetchUserData()
    document.addEventListener('click', clickListener)
})

onBeforeUnmount(() => {
    document.removeEventListener('click', clickListener)
})

const showExamDropdown = () => {
    shouldFocusFirstCard.value = true
    showDropdown.value = true
}

const hideDropdownAndFocusBtn = () => {
    showDropdown.value = false

    if (shouldFocusFirstCard.value) {
        nextTick(() => {
            const dropdownBtnEl = dropdownBtn.value?.$el as HTMLElement | undefined
            dropdownBtnEl?.focus()
            shouldFocusFirstCard.value = false
        })
    }
}

const openAddExamSidePanel = () => {
    showDropdown.value = false
    showAddExamSidePanel.value = true
}

const clickListener = (e: MouseEvent) => {
    if (showDropdown.value) {
        const targetEl = e.target as HTMLElement
        const examMenuDropdownEl = examMenuDropdown.value?.$el as HTMLElement | undefined
        const dropdownBtnEl = dropdownBtn.value?.$el as HTMLElement | undefined
        if (!dropdownBtnEl?.contains(targetEl) && !examMenuDropdownEl?.contains(targetEl)) {
            showDropdown.value = false
        }
    }
}

const clickExamCard = async (exam: Study.Class.ExamMetadataJSON | ClientExam) => {
    if (exam.objectId !== props.currentExamMetadata.objectId) {
        isLoading.value = true
        examMetadataModule.state.isSwitchingExams = true
        hideDropdownAndFocusBtn()
        await userModule.actions.updateCurrentExam(exam.objectId)
        // make sure when we switch exams we clear out activeQuiz
        // this helps if a user had an activeQuiz to resume for another exam
        quizModule.actions.updateActiveQuiz(null)
        emit('examChanged')
        examMetadataModule.state.isSwitchingExams = false
        isLoading.value = false
    } else {
        hideDropdownAndFocusBtn()
    }
}
</script>

<template>
    <div class="exam-menu">
        <AddExamSidePanel
            v-if="showAddExamSidePanel"
            key="examMenu"
            @close="showAddExamSidePanel = false"
        />
        <Portal v-if="isLoading" to="modal">
            <ModalContainer key="loadingExamModal" :is-dark-mode="isDarkMode">
                <template #modal>
                    <Modal
                        v-dark
                        class="exam-menu__loading"
                        :is-dark-mode="isDarkMode"
                    >
                        <img
                            v-if="isDarkMode"
                            class="exam-menu__loading-gif"
                            src="@/assets/loading/High-Five-animation-2DM.gif"
                        >
                        <img
                            v-else
                            class="exam-menu__loading-gif"
                            src="@/assets/loading/High-Five-animation-2.gif"
                        >
                        <div class="exam-menu__loading-title">
                            Let's do this!
                        </div>
                        <div class="exam-menu__loading-subtitle">
                            Loading your exam...
                        </div>
                    </Modal>
                </template>
            </ModalContainer>
        </Portal>
        <ExamCard
            v-if="currentExamMetadata"
            ref="dropdownBtn"
            :exam="currentExamMetadata"
            theme="silver"
            class="exam-menu__selected-exam"
            :show-menu-arrow="true"
            role="button"
            :aria-expanded="showDropdown ? 'true' : 'false'"
            :is-focusable="true"
            :tabindex="showDropdown ? -1 : 0"
            @click="showDropdown = true"
            @keydown.enter="showExamDropdown()"
            @keydown.esc="hideDropdownAndFocusBtn()"
            @mousedown.prevent
        />
        <ExamMenuDropdown
            v-if="showDropdown"
            ref="examMenuDropdown"
            :exam-metadata="examMetadata"
            :current-exam-metadata="currentExamMetadata"
            :focus-first-card="shouldFocusFirstCard"
            @addExam="openAddExamSidePanel"
            @clickExamCard="clickExamCard"
            @close="hideDropdownAndFocusBtn()"
        />
    </div>
</template>

<style lang="scss" scoped>
.exam-menu {
    position: relative;
    height: 54px;
    color: $brand-black;

    @include breakpoint(brown-bear) {
        width: 268px;
    }

    @include breakpoint(black-bear) {
        width: calc(100% - 54px);
    }

    &__loading {
        position: absolute;
        top: 143px;
        left: 50%;
        transform: translateX(-50%);
        padding: 60px;
        display: flex;
        flex-direction: column;
        align-items: center;
        color: $brand-black;
        font-weight: 600;

        &--dark {
            color: $white;
            background: $charcoal;
        }

        @include breakpoint(black-bear) {
            top: 50%;
            transform: translate(-50%, -50%);
        }
    }

    &__loading-gif {
        height: 184px;
        margin-bottom: 19px;
        margin-right: -20px;
    }

    &__loading-title {
        font-size: 16px;
        line-height: 19px;
    }

    &__loading-subtitle {
        font-size: 14px;
        line-height: 17px;
        opacity: 0.75;
    }

    &__selected-exam {
        border-radius: 6px;
        max-width: 100%;
        border: 1px solid transparent;

        @include breakpoint(black-bear) {
            padding: 0 0 0 4px;
            border: none;
        }

        &:focus::before {
            border: 1px solid $banana-bread;
        }
    }
}
</style>
